import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { faqs, fuboSportsFAQs, ppvFAQs, svodFAQs } from 'src/constants/faqs'
import { useSelector } from 'react-redux'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import {
  body,
  borderRadius,
  colors,
  heading,
  maxWidth,
  mediaQuery,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import { SectionContainer, SectionHeader } from 'src/components/common/SharedComponents'
import { ArrowDown } from 'src/components/icons/Arrows'

const FAQSection = ({
  lang = 'en',
  market = 'us',
  team,
  additionalFAQs,
  foxRedirect,
  removeCable,
  canadaEnt,
  faqListOverride,
  isSvod,
  isPPV,
  isFuboSports,
  isDarkMode = false,
}) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const [activeFaqsList, setActiveFaqsList] = useState([])
  const [openIndexes, setOpenIndexes] = useState([])
  useEffect(() => {
    let faqsList = []
    if (faqListOverride) {
      faqsList = faqListOverride
    } else {
      if (isSvod) {
        faqsList = [...svodFAQs[market][lang]].map((faq) => ({ ...faq }))
      } else if (isPPV) {
        faqsList = [...ppvFAQs[market][lang]].map((faq) => ({ ...faq }))
      } else if (isFuboSports) {
        faqsList = [...fuboSportsFAQs[market][lang]].map((faq) => ({ ...faq }))
      } else {
        faqsList = [...faqs[market][lang]].map((faq) => ({ ...faq }))
      }
      if (team) {
        // add team FAQ
        const teamFAQ = {
          question: `Can I watch ${team} online without cable?`,
          answer: `Yes, you can watch ${team} live online without cable with Fubo.`,
        }
        faqsList.splice(1, 0, teamFAQ)
      }

      if (canadaEnt) {
        const soccerIndex = faqsList.findIndex((faq) => faq.id === 'soccer')
        if (soccerIndex !== -1) {
          faqsList[soccerIndex].answer = (
            <>
              Yes! Fubo networks broadcast Premier League, Ligue 1, Serie A, Coppa Italia, Süper
              Lig, Canadian World Cup Qualifiers and many other leagues and tournaments.
              <span style={{ fontSize: '.7em', fontStyle: 'italic', display: 'block' }}>
                *Sports and Premium plans only.
              </span>
            </>
          )
        }
      }
      if (removeCable) {
        const nflIndex = faqsList.findIndex((faq) => faq.id === 'nfl')
        if (nflIndex !== -1) {
          faqsList.splice(nflIndex, 1)
        }
        const whatIsFuboIndex = faqsList.findIndex((faq) => faq.id === 'what-is-fubo')
        if (whatIsFuboIndex !== -1) {
          if (lang === 'es') {
            faqsList[whatIsFuboIndex].answer =
              'Fubo es un servicio que transmite televisión en vivo a través de Internet. Mira tus equipos favoritos, programas de cadenas, noticias y películas en más de 200 canales. Además de entretenimiento on-demand que incluye series de televisión completas.'
          } else {
            faqsList[whatIsFuboIndex].answer =
              'Fubo is a service that broadcasts live TV over the internet. Watch your favorite teams, network shows, news and movies on 200+ channels. Plus on-demand entertainment including full TV series.'
          }
        }
        const collegeFootballIndex = faqsList.findIndex((faq) => faq.id === 'college-football')
        if (collegeFootballIndex !== -1) {
          if (lang === 'es') {
            faqsList[collegeFootballIndex].question = '¿Cómo puedo ver fútbol universitario online?'
            faqsList[collegeFootballIndex].answer =
              'Para ver fútbol universitario, simplemente inicia una prueba gratuita de Fubo. Puede comenzar a mirar inmediatamente en su televisor, teléfono, computadora y más. No existe contrato.'
          } else {
            faqsList[collegeFootballIndex].question = 'How can I watch college football online?'
            faqsList[collegeFootballIndex].answer =
              "To watch college football, just start a free trial of Fubo. You can start watching immediately on your TV, phone, computer and more. There's no contract."
          }
        }
        const howMuchIndex = faqsList.findIndex((faq) => faq.id === 'how-much')
        if (howMuchIndex !== -1) {
          if (lang === 'es') {
            faqsList[howMuchIndex].answer =
              'Los precios varían según su plan, región y complementos seleccionados. Nuestro plan más popular en los Estados Unidos incluye más de 200 canales de televisión y deportes en vivo.'
          } else {
            faqsList[howMuchIndex].answer =
              'Prices vary based on your plan, region and selected add-ons. Our most popular plan in the United States includes 200+ channels of live sports and TV.'
          }
        }
      }

      if (foxRedirect) {
        const nflIndex = faqsList.findIndex((faq) => faq.id === 'nfl')
        const collegeFootballIndex = faqsList.findIndex((faq) => faq.id === 'college-football')
        const howMuchIndex = faqsList.findIndex((faq) => faq.id === 'how-much')
        if (nflIndex !== -1) {
          faqsList[nflIndex].question = 'How can I watch the NFL online?'
          faqsList[nflIndex].answer =
            "To watch the NFL, just start a free trial of Fubo. You can start watching immediately on your TV, phone, computer and more. There's no contract."
        }
        if (collegeFootballIndex !== -1) {
          faqsList[collegeFootballIndex].question = 'How can I watch college football online?'
          faqsList[collegeFootballIndex].answer =
            "To watch college football, just start a free trial of Fubo. You can start watching immediately on your TV, phone, computer and more. There's no contract."
        }
        if (howMuchIndex !== -1) {
          faqsList[howMuchIndex].answer = (
            <>
              Prices vary based on your plan, region and selected add-ons. Our most popular plan in
              the United States includes 200+ channels of live sports and TV for half the cost of
              the average monthly cable bills.{' '}
              <span style={{ fontSize: '.7em', fontStyle: 'italic', display: 'block' }}>
                *Cord Cutter News, March 2020
              </span>
            </>
          )
        }
      }
    }

    if (additionalFAQs) {
      faqsList.splice(1, 0, ...additionalFAQs)
    }

    setOpenIndexes(Array(faqsList.length).fill(false))
    setActiveFaqsList(faqsList)
  }, [lang, market, additionalFAQs, faqListOverride, isSvod, team, canadaEnt, removeCable])

  const toggleFAQ = (index) => {
    // Toggle the state for only the clicked item
    const updatedIndexes = openIndexes.map((item, idx) => {
      return index === idx ? !item : item
    })
    setOpenIndexes(updatedIndexes)
    // Send analytics
    fireUIInteractionEvent(
      {
        element: activeFaqsList[index].question,
        component: 'faq_item_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const title = lang === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'

  const inquiry =
    lang === 'es' ? (
      <>
        Para cualquier duda o pregunta, visita nuestro{' '}
        <a href="https://support.fubo.tv/hc/es" target="_blank" rel="noreferrer">
          Centro de Ayuda
        </a>
      </>
    ) : (
      <>
        Still have questions? Visit our{' '}
        <a href="https://support.fubo.tv/" target="_blank" rel="noreferrer">
          Help Center
        </a>{' '}
        to find more answers and contact us.
      </>
    )

  const faqFooterText = {
    en: {
      headline: 'Still have questions? Expert help, just a click away.',
      inquiry: (
        <>
          For technical questions or getting help with your account, visit our{' '}
          <a href="https://support.fubo.tv/" target="_blank" rel="noreferrer">
            Help Center
          </a>{' '}
          to find more answers and contact us.
        </>
      ),
      footnote:
        'Agent hours of availability: 10:00 AM - 10:00PM ET - 7 days a week. Holiday hours may vary.',
    },
    es: {
      headline: '¿Tienes alguna pregunta? Nuestro equipo de soporte está a tu disposición.',
      inquiry: (
        <>
          Para soporte técnico o ayuda con tu cuenta, visita nuestro{' '}
          <a href="https://support.fubo.tv/" target="_blank" rel="noreferrer">
            Centro de Ayuda
          </a>
        </>
      ),
      footnote:
        'Horario de atención: De 10:00 AM a 10:00 PM (ET), todos los días. Los horarios en días festivos pueden variar.',
    },
  }

  const halfwayIndex = Math.ceil(activeFaqsList.length / 2)
  const column1 = activeFaqsList.slice(0, halfwayIndex)
  const column2 = activeFaqsList.slice(halfwayIndex)

  const renderFAQColumn = (column, offset) => (
    <ul className="faq-column">
      {column.map((faq, index) => {
        const absoluteIndex = index + offset
        return (
          <li key={absoluteIndex} className="faq-item" onClick={() => toggleFAQ(absoluteIndex)}>
            <div className="faq-question">
              <h4>{faq.question}</h4>
              <div className={openIndexes[absoluteIndex] ? 'faq-icon visible' : 'faq-icon'}>
                <ArrowDown fill={colors.blue[80]} />
              </div>
            </div>
            <div className={openIndexes[absoluteIndex] ? 'faq-answer visible' : 'faq-answer'}>
              <p>{faq.answer}</p>
            </div>
          </li>
        )
      })}
    </ul>
  )

  return (
    <SectionContainer backgroundColor={isDarkMode ? colors.slate[30] : 'transparent'}>
      <StyledFAQSection isDarkMode={isDarkMode}>
        <div className="faq-wrapper">
          <SectionHeader title={title} variant={isDarkMode ? 'light' : 'dark'} />
          <div className="faq-body">
            <div className="faq-content">
              {renderFAQColumn(column1, 0)}
              {renderFAQColumn(column2, halfwayIndex)}
            </div>
          </div>
          <div className="faq-footer">
            <div className="faq-footer-head">
              <h3>{faqFooterText[lang].headline}</h3>
            </div>
            <div className="faq-inquiry">{faqFooterText[lang].inquiry}</div>

            <div className="faq-footnote">{faqFooterText[lang].footnote}</div>
          </div>
        </div>
      </StyledFAQSection>
    </SectionContainer>
  )
}

export default FAQSection

const breakpoint = 1000
const desktopBreak = breakpoint + 'px'

// 3:2 Figma to Mobile (calculate 2/3 of figma sizing for mobile)

const StyledFAQSection = styled.section`
  overflow: hidden;
  padding: ${spacing.xl} 0;
  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} 0;
  }

  a {
    color: ${colors.blue[80]};
    text-decoration-line: underline;
  }

  .faq-wrapper {
    max-width: ${maxWidth.desktop};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing.md};
    ${mediaQuery.tablet} {
      gap: ${spacing.xl};
    }
  }

  .faq-body {
    width: 100%;
    .faq-content {
      display: grid;
      grid-template-columns: 1fr;
      gap: ${spacing.md};

      ${mediaQuery.tablet} {
        grid-template-columns: 1fr 1fr;
      }

      .faq-column {
        display: flex;
        flex-direction: column;
        gap: ${spacing.md};

        ${mediaQuery.tablet} {
          gap: ${spacing.lg};
        }
        ${mediaQuery.desktop} {
          gap: ${spacing.md};
        }
      }

      .faq-item {
        background: ${(props) => (props.isDarkMode ? colors.slate[10] : colors.slate[190])};
        display: flex;
        flex-direction: column;
        width: 100%;
        box-sizing: border-box;
        cursor: pointer;
        border-radius: ${borderRadius.md};
        padding: ${spacing.lg};

        .faq-question {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: ${spacing.xxxs};
          h4 {
            color: ${(props) => (props.isDarkMode ? colors.slate[190] : colors.slate[60])};
            ${typographyMixin(heading.sm)}

            @media screen and (max-width: 500px) {
              max-width: 300px;
            }
          }
          .faq-icon {
            display: flex;
            &.visible {
              transform: rotate(180deg);
            }
          }
        }

        .faq-answer {
          padding-right: 22px;
          max-height: 0;
          overflow: hidden;
          @media (min-width: ${desktopBreak}) {
            padding-right: 34px;
          }
          p {
            color: ${(props) => (props.isDarkMode ? colors.slate[160] : colors.slate[60])};
            ${typographyMixin(body.sm)}
          }

          &.visible {
            display: block;
            max-height: 600px;
            transition: max-height 0.8s;
          }
        }
      }
    }
  }
  .faq-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing.md};
    ${mediaQuery.tablet} {
      gap: ${spacing.lg};
    }

    max-width: 900px;

    .faq-footer-head {
      h3 {
        text-align: center;
        color: ${(props) => (props.isDarkMode ? colors.white : 'inherit')};
        ${typographyMixin(heading.sm)}
        ${mediaQuery.tablet} {
          ${typographyMixin(heading.md)}
        }
      }
    }
    .faq-inquiry {
      border-radius: 14px;
      background: ${(props) => (props.isDarkMode ? colors.slate[60] : colors.slate[190])};
      padding: ${spacing.md};
      width: 100%;
      box-sizing: border-box;
      color: ${(props) => (props.isDarkMode ? colors.slate[100] : colors.slate[60])};
      text-align: center;
      ${typographyMixin(body.sm)}
      ${mediaQuery.tablet} {
        border-radius: ${borderRadius.md};
        padding: ${spacing.md};
      }
    }
    .faq-footnote {
      color: ${colors.slate[100]};
      text-align: center;
      ${typographyMixin(body.xs)}
      ${mediaQuery.tablet} {
        ${typographyMixin(body.sm)}
      }
    }
  }
`
